import { redactUrl } from '@/lib/diagnostics/redaction';

export type UnhandledError = {
  detail: string;
  occurredAt: string;
  pageUrl: string;
};

let currentError: UnhandledError | null = null;
const listeners = new Set<() => void>();

const IGNORED_MESSAGE_PREFIXES = ['ResizeObserver loop'];
const WEBKIT_EMPTY_RANGES_MESSAGE = "ReferenceError: Can't find variable: EmptyRanges";

function hasIgnoredMessage(message: string): boolean {
  return IGNORED_MESSAGE_PREFIXES.some((prefix) => message.startsWith(prefix));
}

export function describeError(error: unknown): string {
  if (error instanceof Error) {
    const summary = error.message === '' ? error.name : `${error.name}: ${error.message}`;

    if (error.stack === undefined || error.stack === '') {
      return summary;
    }

    return error.stack.startsWith(summary) ? error.stack : `${summary}\n${error.stack}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function subscribeToUnhandledErrors(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUnhandledError(): UnhandledError | null {
  return currentError;
}

export function reportUnhandledError(error: unknown, context: { occurredAt?: Date; pageUrl?: string } = {}): void {
  const detail = describeError(error);

  if (detail === '' || hasIgnoredMessage(detail)) {
    return;
  }

  if (currentError?.detail === detail) {
    return;
  }

  currentError = {
    detail,
    occurredAt: (context.occurredAt ?? new Date()).toISOString(),
    pageUrl: context.pageUrl ?? window.location.href,
  };
  listeners.forEach((listener) => listener());
}

export function dismissUnhandledError(): void {
  if (currentError === null) {
    return;
  }

  currentError = null;
  listeners.forEach((listener) => listener());
}

function providedNumber(value: number): string {
  return value > 0 ? String(value) : '(not provided by browser)';
}

function hasMissingSource(filename: string | undefined): boolean {
  return filename === undefined || filename === '' || filename === 'undefined';
}

function describeErrorEvent(event: ErrorEvent): string {
  const details = [
    'Unhandled runtime error',
    `Message: ${event.message || '(not provided by browser)'}`,
    `Source: ${hasMissingSource(event.filename) ? '(not provided by browser)' : redactUrl(event.filename)}`,
    `Line: ${providedNumber(event.lineno)}`,
    `Column: ${providedNumber(event.colno)}`,
  ];

  if (event.error === null || event.error === undefined) {
    details.push(
      'Error object: unavailable',
      'Browser note: browser did not provide details for this global error; cross-origin protection can cause this.',
    );
  } else {
    details.push(`Error object:\n${describeError(event.error)}`);
  }

  return details.join('\n');
}

function isWebKitMediaControlsError(event: ErrorEvent): boolean {
  const errorDetail = event.error == null ? '' : describeError(event.error);

  return (
    event.message === WEBKIT_EMPTY_RANGES_MESSAGE &&
    hasMissingSource(event.filename) &&
    errorDetail.includes('played@') &&
    errorDetail.includes('syncControl@')
  );
}

function isIgnoredErrorEvent(event: ErrorEvent): boolean {
  return hasIgnoredMessage(event.message);
}

function isFullyOpaqueScriptError(event: ErrorEvent): boolean {
  return (
    event.message === 'Script error.' &&
    hasMissingSource(event.filename) &&
    event.lineno === 0 &&
    event.colno === 0 &&
    event.error == null
  );
}

function describeUnhandledRejection(event: PromiseRejectionEvent): string {
  return `Unhandled promise rejection\nReason:\n${describeError(event.reason)}`;
}

function resourceUrl(target: HTMLScriptElement | HTMLLinkElement): string {
  const url = target instanceof HTMLScriptElement ? target.src : target.href;
  return url === '' ? '(not provided by browser)' : redactUrl(url);
}

function describeCriticalResourceError(event: Event): string | undefined {
  if (event.target instanceof HTMLScriptElement) {
    return [
      'Critical resource failed to load',
      'Element: script',
      `Type: ${event.target.type || 'classic'}`,
      `Source: ${resourceUrl(event.target)}`,
    ].join('\n');
  }

  if (event.target instanceof HTMLLinkElement && event.target.rel === 'stylesheet') {
    return ['Critical resource failed to load', 'Element: stylesheet', `Source: ${resourceUrl(event.target)}`].join(
      '\n',
    );
  }

  return undefined;
}

export function installUnhandledErrorReporting(): () => void {
  const onError = (event: Event) => {
    if (event instanceof ErrorEvent) {
      if (isIgnoredErrorEvent(event) || isWebKitMediaControlsError(event)) {
        return;
      }

      if (isFullyOpaqueScriptError(event)) {
        console.debug('[UnhandledErrorReporting] Ignored fully opaque Script error event.');
        return;
      }

      reportUnhandledError(describeErrorEvent(event));
      return;
    }

    const detail = describeCriticalResourceError(event);
    if (detail !== undefined) {
      reportUnhandledError(detail);
    }
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent) =>
    reportUnhandledError(describeUnhandledRejection(event));

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    window.removeEventListener('error', onError, true);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}
